import { Link } from "react-router-dom";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import MobileCTA from "@/components/site/MobileCTA";
import ScrollScanner from "@/components/ScrollScanner";
import Reveal from "@/components/site/Reveal";
import RelatedLinks from "@/components/site/RelatedLinks";
import RelatedGuides from "@/components/site/RelatedGuides";
import Breadcrumbs from "@/components/site/Breadcrumbs";
import { BRAND } from "@/lib/brand";
import { trackPhoneCallLead } from "@/lib/ads";
import { useSeo } from "@/lib/useSeo";
import { useJsonLd } from "@/lib/useJsonLd";

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const DOMAIN = "https://meridianbedbug.ca";

const PROPERTY_TYPES = {
  condos: {
    label: "Condos",
    guideKeyword: "condo",
    title: "Bed Bug Control & Treatment for Condos in Vancouver & the Lower Mainland",
    description: "Specialized bed bug treatment for condos and condo units across Vancouver and the Lower Mainland — signs, what to prepare, and how treatment works in multi-unit buildings.",
    intro: "Condos are one of the most common bed bug settings in Vancouver. With shared walls, hallways and electrical pathways between suites, bed bugs can spread between units faster than owners expect — and a single-unit report doesn't always mean a single-unit problem.",
    why: "Condo living means a neighbour on the other side of every shared wall. Bed bugs travel along electrical conduit, plumbing chases and baseboard gaps to reach connected suites, so treating only one unit often leads to a quick return. A specialist approach treats your unit thoroughly and — where arranged — coordinates with strata or property management for adjoining units.",
    faqs: [
      { q: "Can bed bugs spread between condo units?", a: "Yes. In condos, bed bugs can move between connected suites through shared walls and electrical pathways, so early action and — where arranged — coordination across units matters." },
      { q: "Do I need to tell my strata?", a: "Not always, but where bed bugs may have spread between units, coordination with strata or property management can prevent a quick return. We can advise on this during booking." },
      { q: "Is the service discreet in a condo building?", a: "Yes. Our team works with discretion — unmarked service, respectful communication and professional application — so neighbours and other residents aren't drawn into the situation." },
    ],
  },
  apartments: {
    label: "Apartments",
    guideKeyword: "apartment",
    title: "Bed Bug Control & Treatment for Apartments in Vancouver & the Lower Mainland",
    description: "Specialized bed bug treatment for apartments and rental suites across Vancouver and the Lower Mainland — signs, preparation, treatment and discreet follow-up for tenants and landlords.",
    intro: "Apartments and rental suites are high-risk for bed bugs because of how closely units sit together. Once introduced — often through travel or second-hand furniture — bed bugs spread along shared walls and corridors, and an infestation in one suite can become a building issue.",
    why: "Apartment buildings have many shared entry points: electrical outlets, plumbing chases and hallway walls. A specialist treatment targets the suite thoroughly, and for property managers we can coordinate across units so the problem isn't simply pushed next door.",
    faqs: [
      { q: "Who is responsible for bed bug treatment in a rental apartment?", a: "Responsibility can vary by lease and situation. We work with both tenants and landlords — share your details when booking and we'll help clarify the path forward." },
      { q: "Do bed bugs spread between apartment units?", a: "Yes. In apartments, bed bugs can move between connected suites through shared walls and electrical pathways, so early action and coordination across units matters." },
      { q: "Will my neighbours know I called a treatment service?", a: "No. Our service is discreet — unmarked, respectful and professional — so neighbours and other residents aren't drawn into the situation." },
    ],
  },
  hotels: {
    label: "Hotels",
    guideKeyword: "hotel",
    title: "Bed Bug Control & Treatment for Hotels in Vancouver & the Lower Mainland",
    description: "Discreet, specialist bed bug treatment for hotels across Vancouver and the Lower Mainland — guest-room treatment, documentation and follow-up to protect reputation and operations.",
    intro: "For a hotel, a single guest complaint can snowball into reviews, refund requests and reputational damage. Speed and discretion protect more than a room — they protect the brand. Meridian acts as a professional treatment partner for hospitality operations.",
    why: "Hotel rooms see constant turnover, high luggage traffic and tightly packed sleeping areas — ideal conditions for bed bugs to hitchhike between rooms. A specialist response treats affected rooms promptly, targets where bed bugs actually hide, and follows up to break the life cycle.",
    faqs: [
      { q: "How fast can you treat a hotel room?", a: "We aim to schedule treatments as quickly as possible. Share your details and we'll confirm the earliest appointment for your property." },
      { q: "Is the treatment discreet for guests?", a: "Yes. Our service is unmarked, respectful and professional, carried out to minimise disruption to guests and operations." },
      { q: "Do you provide treatment documentation?", a: "Yes — we can provide treatment documentation to support your operational and reporting needs. Let us know your requirements when booking." },
    ],
  },
  "short-term-rentals": {
    label: "Airbnb & Short-Term Rentals",
    guideKeyword: "rental",
    title: "Bed Bug Control & Treatment for Airbnb & Short-Term Rentals in Vancouver & the Lower Mainland",
    description: "Fast, discreet bed bug treatment for Airbnb and short-term rental hosts across Vancouver and the Lower Mainland — protect guests, ratings and your listing.",
    intro: "Short-term rentals face high guest turnover and constant luggage traffic — the fastest way bed bugs arrive and spread. A single report can mean cancellations, poor reviews and a listing's decline. Meridian provides fast, discreet treatment to get a short-term rental back to booking quickly.",
    why: "For hosts, speed is everything. A specialist treatment targets where bed bugs hide in mattresses, frames and upholstery, coordinates around your calendar where possible, and follows up to break the life cycle — so a guest issue doesn't become a business problem.",
    faqs: [
      { q: "How fast can you treat an Airbnb or short-term rental?", a: "We aim to schedule treatments as quickly as possible. Share your availability and we'll work to get the unit back to booking-ready fast." },
      { q: "Will my guests or neighbours know?", a: "No. Our service is unmarked, respectful and discreet, so the situation stays private." },
      { q: "Can you work around my booking calendar?", a: "We'll do our best to schedule around your calendar — share your dates when booking and we'll confirm a window that works." },
    ],
  },
  homes: {
    label: "Homes",
    guideKeyword: "home",
    title: "Bed Bug Control & Treatment for Homes in Vancouver & the Lower Mainland",
    description: "Specialized bed bug treatment for detached homes, townhomes and rental houses across Vancouver and the Lower Mainland — signs, preparation, treatment and follow-up.",
    intro: "Bed bugs reach detached homes and townhomes through travel, visitors and second-hand furniture. Once inside, they hide deep in mattress seams, bed frame joints and baseboard gaps — and a rushed, general spray rarely reaches all of them.",
    why: "A home treatment needs proper preparation and a specialist approach that targets where bed bugs actually live. After booking you receive clear prep instructions, treatment is applied to the right hiding spots, and a follow-up is arranged where the program calls for it to break the life cycle.",
    faqs: [
      { q: "How do bed bugs get into a detached home?", a: "Most often through travel, guests or second-hand furniture and clothing. Bed bugs hitchhike — they don't need an attached neighbour to reach a home." },
      { q: "Do I need to throw out my mattress?", a: "Not usually. Mattresses are commonly treated rather than discarded. We'll advise based on the level of activity and the mattress condition." },
      { q: "Will I need a follow-up treatment?", a: "Where the treatment program calls for it, a follow-up targets any newly hatched insects missed by the first pass to break the life cycle." },
    ],
  },
};

const PROPERTY_ORDER = ["condos", "apartments", "hotels", "short-term-rentals", "homes"];

export default function PropertyTypePage({ type }) {
  const data = PROPERTY_TYPES[type];
  const path = `/bed-bug-treatment/${type}`;

  useSeo({
    title: data ? `${data.title} | Meridian` : "Bed Bug Treatment | Meridian",
    description: data?.description || "Specialized bed bug treatment across Vancouver and the Lower Mainland.",
    path,
    type: "article",
  });

  useJsonLd("property-jsonld", data ? {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Bed Bug Treatment",
    "name": data.title,
    "description": data.description,
    "provider": { "@type": "PestControl", "name": "Meridian Bed Bug Treatment", "telephone": BRAND.phoneTel, "areaServed": ["Vancouver and the Lower Mainland"] },
    "areaServed": [{ "@type": "City", "name": "Vancouver" }],
    "url": DOMAIN + path
  } : {});

  useJsonLd("property-faq-jsonld", data ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": data.faqs.map((f) => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } }))
  } : {});

  if (!data) return null;

  return (
    <div className="overflow-x-hidden pb-16 lg:pb-0">
      <ScrollScanner />
      <Navbar />
      <main id="main-content">
        <section className="bg-background px-5 pt-32 pb-8 lg:px-8 lg:pt-40">
          <div className="mx-auto max-w-3xl">
            <Reveal>
              <Breadcrumbs items={[{ name: "Home", to: "/" }, { name: "Property Types", to: "/bed-bug-treatment-areas" }, { name: data.label }]} className="mb-4" />
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-accent">Property Type</span>
              <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-primary lg:text-4xl">{data.title}</h1>
              <p className="mt-5 text-base leading-relaxed text-muted-foreground">{data.intro}</p>
            </Reveal>
          </div>
        </section>

        <section className="bg-background px-5 pt-12 pb-24 lg:px-8 lg:pt-16 lg:pb-32">
          <div className="mx-auto max-w-3xl space-y-8 text-base leading-relaxed text-muted-foreground">
            <Reveal>
              <div className="max-w-none space-y-6">
                <h2 className="font-heading text-xl font-bold text-primary">Why this property type needs a specialist</h2>
                <p>{data.why}</p>
                <h2 className="font-heading text-xl font-bold text-primary">Signs of bed bugs</h2>
                <p>Watch for itchy bites that appear in lines or clusters after sleeping, dark rust-coloured spots on sheets and mattress seams, shed translucent skins near sleeping areas, and a faint sweet musty smell in heavily infested rooms. You may also spot live insects along mattress piping and behind headboards.</p>
                <h2 className="font-heading text-xl font-bold text-primary">How treatment works</h2>
                <p>After booking, you receive clear preparation instructions — stripping and laundering bedding, reducing clutter near sleeping areas, and moving furniture away from walls where instructed. Treatment targets where bed bugs actually hide: mattress and box spring seams, bed frame joints, baseboard gaps, headboards and surrounding furniture. People and pets leave during treatment and return once the product has dried and the area is safe, following aftercare guidance.</p>
                <h2 className="font-heading text-xl font-bold text-primary">Prevention and follow-up</h2>
                <p>Prevention comes down to awareness after travel, careful inspection of second-hand furniture, and a quick response at the first sign of activity. Where the treatment program calls for it, a follow-up visit targets any newly hatched insects missed by the first pass. Every job is handled with discretion.</p>
              </div>
            </Reveal>
            <Reveal>
              <div className="rounded-lg border border-border bg-muted/30 p-6">
                <h3 className="font-heading text-lg font-bold text-primary">Ready to book {data.label.toLowerCase()} treatment?</h3>
                <p className="mt-2 text-sm text-muted-foreground">Call us or use the booking form on the home page to share a few details about your property.</p>
                <a href={`tel:${BRAND.phoneTel}`} onClick={trackPhoneCallLead} className="mt-4 inline-flex items-center gap-2 font-semibold text-accent">{BRAND.phoneDisplay}</a>
              </div>
            </Reveal>
            <RelatedGuides match={data.guideKeyword} />
            <Reveal>
              <div>
                <h3 className="font-heading text-lg font-bold text-primary">{data.label} treatment — common questions</h3>
                <div className="mt-4 divide-y divide-border border-y border-border">
                  {data.faqs.map((f) => (
                    <div key={f.q} className="py-4">
                      <p className="font-heading text-sm font-semibold text-primary">{f.q}</p>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal>
              <div>
                <h3 className="font-heading text-base font-bold text-primary">Other property types</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {PROPERTY_ORDER.filter((t) => t !== type).map((t) => (
                    <Link key={t} to={`/bed-bug-treatment/${t}`} className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:border-accent/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{PROPERTY_TYPES[t].label}</Link>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal>
              <div>
                <h3 className="font-heading text-base font-bold text-primary">Treatment in your area</h3>
                <div className="mt-3 flex flex-wrap gap-2">
                  {BRAND.areas.map((a) => (
                    <Link key={a} to={a === "Vancouver" ? "/bed-bug-treatment-vancouver" : `/bed-bug-treatment/${slugify(a)}`} className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:border-accent/40 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{a}</Link>
                  ))}
                </div>
                <Link to="/bed-bug-treatment-areas" className="mt-4 inline-flex text-sm font-semibold text-accent">View all service areas</Link>
              </div>
            </Reveal>
          </div>
        </section>
        <RelatedLinks links={[
          { to: "/guide", label: "Bed Bug Guide", title: "Browse the full Bed Bug Guide", desc: "Practical articles on signs, preparation, treatment and prevention.", cta: "Read the guide" },
          { to: "/#booking", label: "Book", title: "Book a treatment", desc: "Send a few details about your property and we'll confirm availability.", cta: "Book treatment" },
          { to: "/bed-bug-treatment-areas", label: "Areas", title: "All service areas", desc: "Cities, neighbourhoods and property types across the Lower Mainland.", cta: "View areas" },
        ]} />
      </main>
      <Footer />
      <MobileCTA />
    </div>
  );
}